import { LanguageFlag } from '@app/components/cms/LanguageFlag';
import { Checkbox } from '@app/components/various/Fields/Checkbox';
import { ChevronRight } from '@app/components/various/Icons';
import { PopoverSheet } from '@app/components/various/Popover/PopoverSheet';
import { LANGUAGE_NAME_MAP } from '@app/constants/translation';
import { cn } from '@app/utils/cn';
import { usePagePublicationActions } from '@app/utils/hooks';
import { usePublishPopupClose } from '@app/utils/hooks/cms/usePublishPopupClose';
import { usePopover } from '@app/utils/hooks/usePopover';
import { isDefined } from '@app/utils/is';
import { Cms, Id, Translations } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHover } from 'react-laag';

import { LanguageItemDescription } from './LanguageItemDescription';

interface Props {
  language: Translations.SupportedLanguagesCodes;
  status: Cms.PageStatus;
  lastModified: Cms.ModificationDetails;
  lastPublished: Cms.ModificationDetails;
  isChecked: boolean;
  isDisabled: boolean;
  onChange: (language: Translations.SupportedLanguagesCodes, isChecked: boolean) => void;
  closeMenu: () => void;
  pageId: Id;
}

export const PublishLanguageItem = (props: Props) => {
  const { language, status, lastModified, lastPublished, isChecked, isDisabled, pageId, onChange, closeMenu } = props;

  const { t } = useTranslation(['cms', 'cmsConfirmationConfig']);
  const [isOpen, setIsOpen] = React.useState(false);

  const { layerProps, triggerProps, layerSide, renderLayer } = usePopover({
    escapeHandlerEnabled: isOpen,
    layerProps: {
      isOpen,
      placement: 'left-start',
      possiblePlacements: ['left-start', 'right-start'],
    },
  });

  const [hasCursor, hoverProps] = useHover();

  const toggleOpen = React.useCallback(() => {
    setIsOpen(opened => !opened);
  }, []);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  usePublishPopupClose({ handleClose, hasCursor });

  const { secondaryMenuActions } = usePagePublicationActions({
    closeMenu: handleClose,
    languages: [language],
    lastPublishedDate: isDefined(lastPublished) ? lastPublished.date : undefined,
    pageId,
    status,
  });

  const languageName = LANGUAGE_NAME_MAP[language];

  const handleChange = React.useCallback(() => {
    onChange(language, !isChecked);
  }, [isChecked, language, onChange]);

  const flagStatus = isChecked ? 'published' : status;

  return (
    <li className="flex min-h-8 items-center hover:bg-neutral-96">
      {renderLayer(
        <PopoverSheet {...layerProps} layerSide={layerSide} isOpen={isOpen}>
          <div className="mb-2 uppercase body-6"> {t('cms:language_version', { language: languageName })}</div>
          <ul className="mt-2">
            {secondaryMenuActions.map(({ description, icon: Icon, key, label, onClick }) => (
              <li key={key} className={cn('flex min-h-8 items-center py-2 hover:bg-neutral-96', { 'h-10': isDefined(description) })}>
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    onClick();
                  }}
                >
                  <span className="flex gap-1 body-2">
                    <Icon className="size-5" />
                    {label}
                  </span>
                  {isDefined(description) && <span className="pl-6 text-neutral-60 body-7">{description}</span>}
                </button>
              </li>
            ))}
          </ul>
        </PopoverSheet>,
      )}
      <Checkbox className="absolute ml-4" isChecked={isChecked} isDisabled={isDisabled} onChange={handleChange}>
        <span className="sr-only">{t('cms:mark_for_publication', { language: languageName })}</span>
      </Checkbox>
      <div {...triggerProps} {...hoverProps} className="w-full">
        <button type="button" onClick={toggleOpen} className="flex w-full items-center justify-between gap-4 py-2 pl-12 pr-4">
          <div>
            <span className="flex items-center gap-1 pt-1 body-2">
              {languageName}
              <LanguageFlag code={language} status={flagStatus} />
            </span>
            <span className="flex gap-1 text-neutral-60 body-7">
              <LanguageItemDescription lastModified={lastModified} lastPublished={lastPublished} status={status} />
            </span>
          </div>
          <ChevronRight />
        </button>
      </div>
    </li>
  );
};
